/**
 * 登录 / 注册页 —— 鉴权入口（独立表面，不含 App Shell）。
 * 演示账号：13800138000 / 123456
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { login, register, tokenStore } from '../api/auth';
import { Icon } from '../components/Icon';
import { toast } from '../components/ui';

type Tab = 'login' | 'register';

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      tab === 'login'
        ? login(email.trim(), password)
        : register(email.trim(), password, nickname.trim() || undefined),
    onSuccess: ({ token, user }) => {
      tokenStore.set(token);
      qc.setQueryData(['me'], { user });
      toast.ok(tab === 'login' ? '登录成功' : '注册成功，已自动登录');
      navigate('/console', { replace: true });
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const submit = (): void => {
    if (mutation.isPending) return;
    mutation.mutate();
  };

  const fillDemo = (): void => {
    setTab('login');
    setEmail('13800138000');
    setPassword('123456');
  };

  return (
    <div className="app app--auth">
      <main className="app-content">
        <div className="auth-hero">
          <span className="shell__mark">
            <Icon name="cpu" size={22} />
          </span>
          <div style={{ marginTop: 12 }}>
            <h1 className="page-title">智能家居控制台</h1>
            <p className="page-sub">注册登录后即可添加设备，并通过小智等语音智能体用 MCP 协议控制。</p>
          </div>
        </div>

        <div className="card card--glow">
          <div className="seg" style={{ width: '100%', marginBottom: 16 }}>
            <button
              className={'seg__btn' + (tab === 'login' ? ' is-active' : '')}
              type="button"
              style={{ flex: 1 }}
              onClick={() => setTab('login')}
            >
              登录
            </button>
            <button
              className={'seg__btn' + (tab === 'register' ? ' is-active' : '')}
              type="button"
              style={{ flex: 1 }}
              onClick={() => setTab('register')}
            >
              注册
            </button>
          </div>

          <form
            className="stack"
            style={{ gap: 14 }}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="field">
              <span className="field__label">账号（邮箱或手机号）</span>
              <input
                className="input"
                value={email}
                autoComplete="username"
                placeholder="13800138000"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <span className="field__label">密码</span>
              <input
                className="input"
                type="password"
                value={password}
                autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                placeholder="至少 6 位"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {tab === 'register' ? (
              <div className="field">
                <span className="field__label">昵称（可选）</span>
                <input
                  className="input"
                  value={nickname}
                  maxLength={24}
                  placeholder="保留为空则自动生成"
                  onChange={(e) => setNickname(e.target.value)}
                />
              </div>
            ) : null}

            <button className="btn btn--primary btn--block" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? '处理中…' : tab === 'login' ? '登录' : '注册并登录'}
            </button>
          </form>

          <div className="row row--between" style={{ marginTop: 14 }}>
            <span className="t-xs dim">演示账号 13800138000 / 123456</span>
            <button className="btn btn--sm btn--ghost" type="button" onClick={fillDemo}>
              一键填入
            </button>
          </div>
        </div>

        <p className="t-xs dim" style={{ marginTop: 16, lineHeight: 1.7 }}>
          「我的设备」是 MCP 控制与第三方同步的唯一数据底座：没有设备就无物可控。
        </p>
      </main>
    </div>
  );
}
